import {
  createContext,
  useMemo,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";

// API calls
import {
  getcombinedWeeklyHours,
  getWorkerTimesheetSignature,
} from "../../services/api/timekeeping";
import { getUser } from "../../services/api/core";

export const SuperTimesheetContext = createContext(null);

export function useSuperTimesheetContext() {
  const context = useContext(SuperTimesheetContext);
  if (!context) {
    throw new Error(
      "useSuperTimesheetContext must be used within an SuperTimesheetProvider"
    );
  }
  return context;
}

export function useSuperTimesheetCtx(WorkerID = null, weekEnding = null) {
  // Shared int context as it is edited and submitted by different components
  const [worker, setWorker] = useState(null);
  const [combinedWeeklyHours, setCombinedWeeklyHours] = useState([]);
  const [workerSignature, setWorkerSignature] = useState(null);
  const hoursConflicts = useRef(null);
  const missingLogs = useRef(null);
  const managerComments = useRef(null);
  const [currentWorkerID, setCurrentWorkerID] = useState(WorkerID || 1);
  const [currentWeekEnding, setCurrentWeekEnding] = useState(
    weekEnding || "2025-08-16"
  );

  // Fetch weekly hours logs, to be compared to worker timesheet hours
  const fetchWorker = useCallback(async () => {
    try {
      const response = await getUser(currentWorkerID);
      setWorker(response.user || null);
    } catch (error) {
      console.error("Error fetching worker:", error);
    }
  }, [currentWorkerID]);

  // Fetch combined worker shifts and hours logs for the week
  const fetchCombinedWeeklyHours = useCallback(async () => {
    try {
      const response = await getcombinedWeeklyHours(
        currentWorkerID,
        currentWeekEnding
      );
      setCombinedWeeklyHours(response.shifts || []);
      hoursConflicts.current = response.conflicts || false;
      missingLogs.current = response.missingLogs || false;
    } catch (error) {
      console.error("Error fetching combined weekly hours:", error);
      return null;
    }
  }, [currentWorkerID, currentWeekEnding]);

  // Fetch the workers signature for the managers approval
  const fetchWorkerSignature = useCallback(async () => {
    try {
      const response = await getWorkerTimesheetSignature(
        currentWorkerID,
        currentWeekEnding
      );
      setWorkerSignature(response.signature || null);
    } catch (error) {
      console.error("Error fetching worker signature:", error);
    }
  }, [currentWorkerID, currentWeekEnding]);

  // Fetch the async data when the component mounts or when WorkerID/weekEnding changes
  useEffect(() => {
    if (currentWorkerID && currentWeekEnding) {
      fetchCombinedWeeklyHours();
      fetchWorkerSignature();
      fetchWorker();
    }
  }, [
    currentWorkerID,
    currentWeekEnding,
    fetchCombinedWeeklyHours,
    fetchWorkerSignature,
    fetchWorker,
  ]); // Re-fetch when WorkerID or weekEnding changes

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      combinedWeeklyHours,
      setCombinedWeeklyHours,
      hoursConflicts,
      missingLogs,
      managerComments,
      weekEnding: currentWeekEnding,
      setCurrentWeekEnding,
      workerSignature,
      setCurrentWorkerID,
      worker,
    }),
    [
      combinedWeeklyHours,
      setCombinedWeeklyHours,
      hoursConflicts,
      missingLogs,
      managerComments,
      currentWeekEnding,
      workerSignature,
      worker,
    ]
  );

  return {
    ...contextValue,
  };
}

export default useSuperTimesheetCtx; // Export the hook as default for backward compatibility
